import { TMarketplace, TBaseConfig } from './types/config';
import { TRegionAPI, TRegionSEO, TLocaleAPI, TLanguage, TLocaleSEO } from './types/locale';
import { TI18nOptions } from './types/webconfig';

// eslint-disable-next-line prefer-destructuring
export const ENV = process.env.ENV as 'prod' | 'development';
export const IS_DOCKER = process.env.DOCKER === 'yes';
export const IS_DOCKER_NON_DEV = IS_DOCKER && process.env.ENV !== 'dev';
// eslint-disable-next-line no-underscore-dangle
export const MARKETPLACE = process.env._MARKETPLACE as TMarketplace;

// eslint-disable-next-line no-underscore-dangle
export const APP = process.env._APP;
export const DOMAIN = process.env.ENV === 'dev' || process.env.ENV === 'staging' ? `${MARKETPLACE}stg` : MARKETPLACE;
// eslint-disable-next-line prefer-destructuring
export const ALT = process.env.ALT;
export const SUBDOMAIN = process.env.ENV === 'dev' ? 'dev' : `www${ALT ? `-${ALT}` : ''}`;

export const IS_BROWSER = typeof window !== 'undefined';
// export const SVC_ENDPOINT = IS_BROWSER ? '/_svc' : `https://api-external${ALT ? `-${ALT}` : ''}.${DOMAIN}.com/_svc`;
export const SVC_ENDPOINT = IS_BROWSER ? '/_svc' : `https://api-external.${DOMAIN}.com/_svc`;

export const CDN_DOMAIN = process.env.CDN_DOMAIN || 'https://a.nooncdn.com';
export const IMG_BASE = `${CDN_DOMAIN}/s/app/com`;
const PRODUCT_IMG_BASE = `${CDN_DOMAIN}`;
// eslint-disable-next-line prefer-destructuring
const GMAPS_API_KEY = process.env.GMAPS_API_KEY;

export const baseConfig: TBaseConfig = {
    regions: {
        ae: {
            seo: 'uae',
            api: 'ae',
            iso: 'AE',
            currencies: {
                en: 'AED',
                ar: 'د.إ.‏',
            },
        },
        sa: {
            seo: 'saudi',
            api: 'sa',
            iso: 'SA',
            currencies: {
                en: 'SAR',
                ar: 'ر.س.‏',
            },
        },
        eg: {
            seo: 'egypt',
            api: 'eg',
            iso: 'EG',
            currencies: {
                en: 'EGP',
                ar: 'جنيه',
            },
        },
    },
    languages: ['ar', 'en'],
    extrai18nLocales: ['ar', 'en', 'ar-eg'],

    endpoints: {
        webconfig: IS_DOCKER_NON_DEV ? 'http://misc-com' : `${SVC_ENDPOINT}/misc`,
        services: SVC_ENDPOINT,
    },
    urls: {
        colorSwatches: `${CDN_DOMAIN}/cms/pages/20181025/0ce4c3a5d392155933d4571ef91fc179`,
        base: `https://${SUBDOMAIN}.${DOMAIN}.com`,
        imgBase: IMG_BASE,
        productImageBase: PRODUCT_IMG_BASE,
    },

    commonHeaders: {
        'Content-Type': 'application/json',
        'X-Platform': 'web',
        'X-CMS': 'v2',
    },

    gtm: {
        ADD_TO: 'add_to_',
        PAGE: 'page_',
        SIGN: 'sign',
        REMOVE_FROM: 'remove_from_',
        SEARCH: 'search',
    },

    assets: {
        images: {
            flags: {
                ae: `${IMG_BASE}/common/images/flags/ae.svg`,
                sa: `${IMG_BASE}/common/images/flags/sa.svg`,
                eg: `${IMG_BASE}/common/images/flags/eg.svg`,
            },
            logos: {
                noon: {
                    en: `${IMG_BASE}/noon/images/logos/noon-black-en.svg`,
                    ar: `${IMG_BASE}/noon/images/logos/noon-black-ar.svg`,
                },
                kul: {
                    en: `${IMG_BASE}/kul/images/logos/kul-white-en.svg`,
                    ar: `${IMG_BASE}/kul/images/logos/kul-white-ar.svg`,
                },
                sivvi: {
                    en: `${IMG_BASE}/sivvi/images/logos/sivvi-en.svg`,
                    ar: `${IMG_BASE}/sivvi/images/logos/sivvi-ar.svg`,
                },
            },
            // #TODO: Get the images from graphic team and move to CDN
            app: {
                appStore: `${IMG_BASE}/common/images/logos/app-store.svg`,
                googlePlay: `${IMG_BASE}/common/images/logos/google-play.svg`,
            },
            consumerRight: {
                en: `${CDN_DOMAIN}/s/misc/2017/consumer_right_logo_1024.png`,
                ar: `${CDN_DOMAIN}/s/misc/2017/consumer_right_logo_1024.png`,
            },
            // Common Images
            statusCross: `${IMG_BASE}/common/images/status-cross-red.svg`,
            statusTickOrange: `${IMG_BASE}/common/images/status-tick-orange.svg`,
            statusTickGreen: `${IMG_BASE}/common/images/status-tick-green.svg`,
            reviewSuccess: `${IMG_BASE}/common/images/review-success.svg`,
        },
        icons: {
            chevronLeft: `${IMG_BASE}/common/icons/chevron-left.svg`,
            chevronRight: `${IMG_BASE}/common/icons/chevron-right.svg`,
            chevronUp: '',
            thumbsUp: `${IMG_BASE}/common/icons/thumbs-up.svg`,
            verified: `${IMG_BASE}/common/icons/verified.svg`,
            unverified: `${IMG_BASE}/common/icons/unverified.svg`,
            nonReturnable: `${IMG_BASE}/common/icons/non-returnable.svg`,
            sadFace: `${IMG_BASE}/common/icons/sad-place.svg`,
            optionChecked: `${IMG_BASE}/common/icons/option-checked.svg`,
            optionUnchecked: `${IMG_BASE}/common/icons/option-unchecked.svg`,
            x: `${IMG_BASE}/common/icons/x.svg`,
            // CC / Payment
            amex: `${IMG_BASE}/common/icons/amex.svg`,
            visa: `${IMG_BASE}/common/icons/visa.svg`,
            mastercard: `${IMG_BASE}/common/icons/mastercard_small.svg`,
            mada: `${IMG_BASE}/common/icons/mada.svg`,
            noonCredits: `${IMG_BASE}/common/icons/noon-credits.svg`,
            emi: `${IMG_BASE}/common/icons/emi.svg`,
            creditCard: `${IMG_BASE}/common/icons/credit-card.svg`,
            cod: `${IMG_BASE}/common/icons/cod.svg`,
            cash: `${IMG_BASE}/common/icons/cash.svg`,
            // Ratings
            starEmpty: `${IMG_BASE}/common/icons/star-empty.svg`,
            starFilled: `${IMG_BASE}/common/icons/star-filled.svg`,
            // password show
            eye: `${IMG_BASE}/common/icons/eye.svg`,
            closedeye: `${IMG_BASE}/common/icons/eye-closed.svg`,
            lock: `${IMG_BASE}/common/icons/lock.svg`,
            freeGift: `${IMG_BASE}/common/icons/free-gift.svg`,
            searchDark: `${IMG_BASE}/common/icons/search-dark.svg`,
            radioOn: `${IMG_BASE}/common/icons/radio-on.svg`,
            radioOff: `${IMG_BASE}/common/icons/radio-off.svg`,
            successTick: `${IMG_BASE}/noon/images/account/success-tick.svg`,
            checkmark: '',
            gift: '',
            share: '',
        },
    },

    map: {
        googleMapAPIKey: `${GMAPS_API_KEY || 'AIzaSyDCce7ZDWT6OmSjx5m3TEVuqCBtquGIavw'}`,
        centralGeoLocations: {
            ae: { lat: 252048201, lng: 552707501 }, // Dubai
            sa: { lat: 247115201, lng: 466732959 }, // Jeddah
            eg: { lat: 299764800, lng: 311313020 }, // Cairo
        },
        defaultZoom: 14,
        precision: 10000000,
        biasingRadius: 20000,
    },
};

// Computed Locale Configurations
export const SEO_REGIONS = Object.keys(baseConfig.regions).map(
    (region: string) => baseConfig.regions[region as TRegionAPI].seo,
);

const seoLocalesArr = SEO_REGIONS.map((seoRegion: TRegionSEO) =>
    baseConfig.languages.map((language: TLanguage) => `${seoRegion}-${language}` as TLocaleSEO),
);
export const SEO_LOCALES = [].concat(...(seoLocalesArr as []));

export const API_REGIONS = Object.keys(baseConfig.regions).map(region => baseConfig.regions[region as TRegionAPI].api);

const apiLocalesArr = API_REGIONS.map((apiRegion: TRegionAPI) =>
    baseConfig.languages.map((language: TLanguage) => `${apiRegion}-${language}` as TLocaleAPI),
);
export const API_LOCALES = [].concat(...(apiLocalesArr as []));

// Common i18n config (server, client)
export const i18nOptions: TI18nOptions = {
    lowerCaseLng: true,
    defaultLanguage: SEO_LOCALES[0],
    otherLanguages: [...SEO_LOCALES, ...baseConfig.extrai18nLocales],
    ignoreRoutes: ['/_next', '/static', '/api', '/hc'],
    whitelist: SEO_LOCALES,
};

export const marketplaceImgBase = `${baseConfig.urls.imgBase}/${MARKETPLACE}`;

export const commonImgBase = `${baseConfig.urls.imgBase}/common`;
